import { bookmakerLogos } from "./bookmakerLogos.js";
import { fairSpread, marketConsensus } from "./edge.js";
import { teamNickname } from "./teamColors.js";
import { getEspnTeamId } from "./teamAbbr.js";

const EASTERN = "America/New_York";

function toNumberOrNull(value) {
  return value == null ? null : Number(value);
}

function formatKickoff(kickoffAt) {
  if (!kickoffAt) return "";

  const kickoff = new Date(kickoffAt);
  const weekday = kickoff.toLocaleString("en-US", {
    timeZone: EASTERN,
    weekday: "short",
  });
  const time = kickoff.toLocaleString("en-US", {
    timeZone: EASTERN,
    hour: "numeric",
    minute: "2-digit",
  });
  const date = kickoff.toLocaleString("en-US", {
    timeZone: EASTERN,
    month: "numeric",
    day: "numeric",
  });

  return `${weekday} ${time} - ${date}`;
}

// The sharp-weighted, de-vigged consensus -- the same number `computeEdge`
// picks the edge side from. An unweighted mean of posted lines names the other
// team on a near-pick'em game, so the card would contradict its own edge.
function marketFavorite(homeTeam, awayTeam, books) {
  const consensus = marketConsensus(books);
  if (typeof consensus !== "number" || Number.isNaN(consensus)) return null;
  if (consensus === 0) return null;

  return consensus < 0 ? homeTeam : awayTeam;
}

// How much the books disagree with EACH OTHER -- a population standard
// deviation of their de-vigged numbers, the same quantity Ruby's
// `MarketConsensus#dispersion` computes.
//
// It used to be the RMS distance from the frozen line, which is a different
// thing wearing a sigma: eleven books all at -3.5 against a frozen -3 read
// 0.500 when they in fact agreed perfectly. The card says how far the market
// has moved from our number three other ways (the points, the tug bar, the
// book tally); the only thing this slot can add is whether the books are of
// one mind -- which is exactly what Home's "Most disagreement" sort wants.
function bookDispersion(books) {
  const priced = books
    .map((book) => fairSpread(book))
    .filter((spread) => typeof spread === "number" && !Number.isNaN(spread));

  // One book cannot disagree with anybody.
  if (priced.length < 2) return 0;

  const mean = priced.reduce((sum, spread) => sum + spread, 0) / priced.length;
  const squares = priced.map((spread) => (spread - mean) ** 2);
  return Math.sqrt(squares.reduce((sum, square) => sum + square, 0) / squares.length);
}

function mapTeam(name, abbr) {
  return {
    // ESPN's own numeric id, not this app's. /api/v1/schedules and
    // /api/v1/injuries both key on it and nothing else in the games payload
    // carries it -- see teamAbbr.js for why this can't just be looked up
    // server-side today.
    id: getEspnTeamId(abbr),
    abbr,
    name,
    shortName: teamNickname(name),
    record: null,
    atsRecord: null,
    // {name, status} when the starting QB is Questionable, Doubtful or Out;
    // null when healthy or unknown. Context only -- nothing ranks on it.
    qbInjury: null,
    logo: null,
  };
}

function mapQbInjury(injury) {
  if (!injury || !injury.status) return null;
  return { name: injury.name ?? null, status: injury.status };
}

function camelInjury(player) {
  return {
    name: player.name,
    position: player.position,
    jersey: player.jersey,
    headshot: player.headshot,
    status: player.status,
    type: player.type,
    shortComment: player.short_comment,
    date: player.date,
  };
}

function camelFormGame(game) {
  return {
    gameId: game.game_id,
    opponent: game.opponent,
    opponentName: game.opponent_name,
    opponentLogo: game.opponent_logo,
    home: game.home,
    pointsFor: game.points_for,
    pointsAgainst: game.points_against,
    result: game.result,
    week: game.week,
    date: game.date,
  };
}

// The ESPN `summary` block, present only on GET /api/v1/games/:id (the list
// endpoint never carries it). Camel-cases the payload and leaves every branch
// null/[] where ESPN was silent, so the detail page can `v-if` on each section.
function mapSummary(summary) {
  if (!summary) return null;

  return {
    espnTeamIds: summary.espn_team_ids ?? null,
    teamAbbrs: summary.team_abbrs ?? null,
    records: summary.records ?? null,
    predictor: summary.predictor
      ? {
          homeWinPct: summary.predictor.home_win_pct,
          awayWinPct: summary.predictor.away_win_pct,
        }
      : null,
    espnOdds: summary.espn_odds
      ? {
          provider: summary.espn_odds.provider,
          homeSpread: summary.espn_odds.home_spread,
          overUnder: summary.espn_odds.over_under,
          details: summary.espn_odds.details,
          favorite: summary.espn_odds.favorite,
          homeMoneyline: summary.espn_odds.home_moneyline,
          awayMoneyline: summary.espn_odds.away_moneyline,
          openHomeSpread: summary.espn_odds.open_home_spread ?? null,
          closeHomeSpread: summary.espn_odds.close_home_spread ?? null,
          homeFavoriteAtOpen: summary.espn_odds.home_favorite_at_open,
        }
      : null,
    injuries: {
      home: (summary.injuries?.home || []).map(camelInjury),
      away: (summary.injuries?.away || []).map(camelInjury),
    },
    form: {
      home: (summary.form?.home || []).map(camelFormGame),
      away: (summary.form?.away || []).map(camelFormGame),
    },
  };
}

export function mapStoredGame(game) {
  const spreads = (game.market_lines || []).map((line) => {
    const homeSpread = toNumberOrNull(line.home_spread);

    return {
      bookmaker: line.bookmaker,
      team: homeSpread == null || homeSpread <= 0 ? game.home_team : game.away_team,
      spread: homeSpread == null ? null : -Math.abs(homeSpread),
      homeSpread,
      // Both prices, because a posted number without its price is only most of
      // the book's opinion — `fairSpread` needs the pair to remove the vig.
      homePrice: toNumberOrNull(line.home_price),
      awayPrice: toNumberOrNull(line.away_price),
      capturedAt: line.captured_at ?? null,
      // When a poll last saw this quote; the server already falls back to
      // captured_at, and so does this for an older payload.
      lastSeenAt: line.last_seen_at ?? line.captured_at ?? null,
      logo: bookmakerLogos[line.bookmaker?.toLowerCase()] || null,
    };
  });

  const fivePicksSpread = toNumberOrNull(game.league_spread);
  const summary = mapSummary(game.summary);

  const homeTeam = mapTeam(game.home_team, game.home_abbr);
  const awayTeam = mapTeam(game.away_team, game.away_abbr);

  // Record and ATS entering this game, computed server-side off our own
  // scores and the pool's frozen lines. Both endpoints carry them, so the
  // card and the game page print the same numbers. null means unknown and
  // renders nothing -- never read it as "0-0".
  homeTeam.record = game.home_record ?? null;
  awayTeam.record = game.away_record ?? null;
  homeTeam.atsRecord = game.home_ats ?? null;
  awayTeam.atsRecord = game.away_ats ?? null;
  homeTeam.qbInjury = mapQbInjury(game.home_qb_injury);
  awayTeam.qbInjury = mapQbInjury(game.away_qb_injury);

  if (summary) {
    if (homeTeam.id == null) homeTeam.id = summary.espnTeamIds?.home ?? null;
    if (awayTeam.id == null) awayTeam.id = summary.espnTeamIds?.away ?? null;
  }

  return {
    id: game.id,
    week: game.week,
    kickoffAt: game.kickoff_at,
    gameDate: formatKickoff(game.kickoff_at),
    // The server already knew these; the mapper just never carried them, so
    // the UI had no way to tell a finished game from an upcoming one.
    status: game.status ?? null,
    // Set once the decision closed; the market rows are a closing line as of
    // this instant, not a live one.
    marketFrozenAt: game.market_frozen_at ?? null,
    completed: game.status === "completed",
    homeScore: toNumberOrNull(game.home_score),
    awayScore: toNumberOrNull(game.away_score),
    fivePicksSpread,
    fivePicksConfirmed: game.league_spread_status === "confirmed",
    favorite: game.favorite,
    marketFavorite: marketFavorite(game.home_team, game.away_team, spreads),
    lineEmailExpected: game.league_spread_expected,
    std: bookDispersion(spreads),
    // Dispersion is a fact about the books alone, so it no longer needs a
    // frozen line -- but it does need two books to be a spread of opinion.
    stdMeasurable: spreads.length > 1,
    homeTeam,
    awayTeam,
    spreads,
    summary,
    pick: game.pick ?? null,
    broadcast: game.broadcast ?? null,
    // Every score in a picked game that has started (Espn::ScoringPlays), for
    // the Picks page's cover chart. Null when not fetched or the fetch failed.
    scoringPlays: game.scoring_plays ?? null,
    venueName: game.venue_name ?? null,
    indoor: game.indoor ?? null,
    neutralSite: game.neutral_site ?? null,
    weatherTemp: toNumberOrNull(game.weather_temp),
    weatherDescription: game.weather_description ?? null,
  };
}
